import React, { memo } from "react";
import {
  X,
  Check,
  Crown,
  Mic,
  Coins,
  Clock,
  Sparkles,
} from "lucide-react";
import { TbMessageFilled } from "react-icons/tb";
import { motion } from "framer-motion";

import { useState, useEffect } from "react";

export const EnhancedSongCard = memo(
  ({
    item,
    index,
    type,
    isFirst,
    newApprovedItems,
    handleDecline,
    handleAccept,
    handlePlayed,
    handleNotYet,
    isHistory,
  }) => {
    const [elapsedTime, setElapsedTime] = useState(0);
    const isNewItem = newApprovedItems?.[item.id];
    const timerTime = 15 * 60 * 1000; // 15 mins in milliseconds
    const approvedTime = new Date(item.approvedAt).getTime();
    const isOverdue =
      type === "approved" &&
      !isHistory &&
      Date.now() - approvedTime > timerTime &&
      !item.isAnnouncement &&
      !item.isFastTrack;
    const isFastTrack = type === "approved" && item.isFastTrack && !isHistory;
    const shouldAnimate = isOverdue || isFastTrack;
    const isPlayed = (item.status || "").toLowerCase() === "played";

    // Tip calculation
    const tipVal =
      item.djTipValueInr ||
      (item.djTipCoins ? Number(item.djTipCoins) : 0) ||
      (item.tipAmount ? Number(item.tipAmount) : 0);
    const hasTip = tipVal > 0;
    const tipStr = tipVal % 1 === 0 ? tipVal : tipVal.toFixed(2);

    useEffect(() => {
      let timer;
      if (isOverdue) {
        timer = setInterval(() => {
          setElapsedTime(Date.now() - approvedTime);
        }, 1000);
      }
      return () => clearInterval(timer);
    }, [isOverdue, approvedTime]);

    const formatTimer = (timeInMs) => {
      const minutes = Math.floor(timeInMs / (1000 * 60));
      const hours = Math.floor(minutes / 60);

      if (hours >= 1) {
        return `More than ${hours} hr${hours > 1 ? "s" : ""}`;
      } else {
        return `More than ${minutes} min${minutes > 1 ? "s" : ""}`;
      }
    };

    const formatItemTime = (timestamp) =>
      timestamp
        ? new Date(timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "--:--";

    const displayTime = isOverdue
      ? formatTimer(elapsedTime)
      : item.approvedAt
        ? formatItemTime(item.approvedAt)
        : formatItemTime(item.updatedAt);

    return (
      <div className={`w-full ${isNewItem ? "bg-green-50/50" : ""}`}>
        <div
          className={`bg-white rounded-2xl p-3.5 sm:p-4 shadow-sm border transition-all duration-200 hover:shadow-md relative overflow-hidden ${
            isOverdue
              ? "border-red-400 ring-1 ring-red-400/30"
              : isFastTrack
                ? "border-amber-400 ring-1 ring-amber-400/30"
                : "border-gray-200/80"
          }`}
        >
          {/* Animated line indicator for Overdue / VIP */}
          {isOverdue ? (
            <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{
                  duration: 1.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
                className="h-full w-full bg-gradient-to-r from-transparent via-red-500 to-transparent"
              />
            </div>
          ) : isFastTrack ? (
            <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{
                  duration: 1.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
                className="h-full w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent"
              />
            </div>
          ) : null}

          {/* Main Content Area */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 w-full">
            {/* Left: Index + Album Art + Song Name & Artist + Badges */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Index Number or Urgent Siren */}
              <div className="flex items-center justify-center flex-shrink-0 w-6 text-center">
                {shouldAnimate ? (
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{
                      duration: 0.5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                    className="text-base font-bold"
                  >
                    🚨
                  </motion.div>
                ) : (
                  <span className="text-sm sm:text-base font-bold text-gray-400">
                    {index + 1}
                  </span>
                )}
              </div>

              {/* Album Art */}
              <img
                src={
                  item.album?.images?.[0]?.url ||
                  "/placeholder.svg?height=56&width=56"
                }
                alt={item.name}
                className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl shadow-xs flex-shrink-0 border border-gray-100"
                loading="lazy"
              />

              {/* Song Title & Artists & Badges */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight truncate">
                    {item.name}
                  </h3>
                  {isFastTrack && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                      <Crown className="w-3 h-3 text-amber-700" /> VIP
                    </span>
                  )}
                  {isOverdue && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
                      🚨 Urgent
                    </span>
                  )}
                </div>

                <p className="text-xs sm:text-sm text-gray-500 font-medium truncate mt-0.5">
                  {item.artists?.map((a) => a.name).join(", ") || "Unknown Artist"}
                </p>
              </div>
            </div>

            {/* Right: Tip Badge + Timestamp + Action Buttons */}
            <div className="flex flex-col sm:flex-row md:flex-col items-end justify-center gap-2 flex-shrink-0 self-center">
              {/* Meta Row: Tip Badge & Timestamp */}
              <div className="flex items-center gap-2 justify-end w-full sm:w-auto">
                {hasTip && (
                  <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-950 shadow-2xs border border-amber-300">
                    <Coins className="h-3 w-3 text-amber-900" />
                    ₹{tipStr} Tip
                  </div>
                )}
                <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
                  {displayTime}
                </span>
              </div>

              {/* Action Buttons */}
              {!isHistory ? (
                <div className="flex items-center gap-2 w-full sm:w-auto mt-0.5">
                  {type === "request" ? (
                    <>
                      <button
                        onClick={() => handleDecline(item.id)}
                        disabled={!isFirst}
                        className={`flex-1 sm:flex-initial px-3.5 py-1.5 border border-red-200 bg-red-50/60 hover:bg-red-100 text-red-600 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all shadow-2xs ${
                          !isFirst ? "opacity-40 cursor-not-allowed" : "hover:border-red-300"
                        }`}
                      >
                        <X className="w-3.5 h-3.5" /> Decline
                      </button>
                      <button
                        onClick={() => handleAccept(item.id)}
                        disabled={!isFirst}
                        className={`flex-1 sm:flex-initial px-4 py-1.5 bg-logo hover:opacity-95 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 shadow-sm shadow-purple-200 transition-all ${
                          !isFirst
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:scale-[1.02] active:scale-[0.98]"
                        }`}
                      >
                        <Check className="w-3.5 h-3.5" /> Accept
                      </button>
                    </>
                  ) : type === "approved" ? (
                    <>
                      <button
                        onClick={handleNotYet}
                        className="flex-1 sm:flex-initial px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all"
                      >
                        <X className="w-3.5 h-3.5 text-gray-500" /> Not Yet
                      </button>
                      <button
                        onClick={() => handlePlayed(item.id)}
                        className={`flex-1 sm:flex-initial px-4 py-1.5 rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 text-white shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98] ${
                          isOverdue
                            ? "bg-red-500 hover:bg-red-600 shadow-red-200"
                            : isFastTrack
                              ? "bg-amber-500 hover:bg-amber-600 shadow-amber-200"
                              : "bg-logo hover:opacity-95 shadow-purple-200"
                        }`}
                      >
                        <Check className="w-3.5 h-3.5" /> Played
                      </button>
                    </>
                  ) : null}
                </div>
              ) : (
                <div
                  className={`px-3 py-1 text-white rounded-lg text-xs font-bold ${
                    isPlayed ? "bg-green-600" : "bg-red-600"
                  }`}
                >
                  {isPlayed ? "Played" : "Rejected"}
                </div>
              )}
            </div>
          </div>

          {/* Banner Section (Tip / Overdue Banner) */}
          {!isHistory && (hasTip || isOverdue) && (
            <div className="w-full mt-2.5 pt-2 border-t border-gray-100">
              {isOverdue && hasTip ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                  <div className="flex items-center gap-2.5 px-3.5 py-2 bg-red-50 border border-red-200 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-red-100 border border-red-200 flex items-center justify-center text-red-600 flex-shrink-0 shadow-2xs">
                      <Clock className="w-3.5 h-3.5 animate-pulse" />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1 leading-none">
                      <span className="text-xs sm:text-sm font-bold text-red-950 whitespace-nowrap">
                        {formatTimer(elapsedTime)} delay
                      </span>
                      <span className="text-[11px] sm:text-xs text-red-600 truncate">
                        • Waiting in queue
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 px-3.5 py-2 bg-amber-50 border border-amber-300 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-700 flex-shrink-0 shadow-2xs">
                      <Coins className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1 leading-none">
                      <span className="text-xs sm:text-sm font-bold text-amber-950 whitespace-nowrap">
                        ₹{tipStr} tip added
                      </span>
                      <span className="text-[11px] sm:text-xs text-amber-700 truncate">
                        • Play to collect
                      </span>
                    </div>
                  </div>
                </div>
              ) : hasTip ? (
                <div className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 bg-gradient-to-r from-amber-50 via-yellow-50/90 to-amber-50 border border-amber-200/90 rounded-xl shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-800 flex-shrink-0 shadow-2xs">
                      <Coins className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-nowrap leading-none">
                      <span className="text-sm sm:text-base font-bold text-amber-950 whitespace-nowrap">
                        ₹{tipStr} tip waiting for you! 🎉
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-amber-800/90 whitespace-nowrap truncate">
                        — Play this song to collect it
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
              ) : isOverdue ? (
                <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 bg-red-50 border border-red-200/90 rounded-xl shadow-2xs">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-100 border border-red-200 flex items-center justify-center text-red-600 flex-shrink-0 shadow-2xs">
                    <Clock className="w-4 h-4 text-red-600 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2 min-w-0 flex-1 flex-nowrap leading-none">
                    <span className="text-sm sm:text-base font-bold text-red-950 whitespace-nowrap">
                      {formatTimer(elapsedTime)} waiting ⏱
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-red-700 whitespace-nowrap truncate">
                      — Try to play this request soon
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export const EnhancedShoutCard = memo(
  ({ shout, index, type = "request", handleNotYet, ShoutoutPlayed }) => {
    const guestName = shout.user?.display_name?.trim() || "Guest";
    const initial = guestName.charAt(0).toUpperCase() || "G";

    // Deterministic avatar fallback color
    const colorIndex =
      Math.abs(
        guestName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      ) % 6;
    const bgColors = [
      "bg-purple-600",
      "bg-pink-600",
      "bg-indigo-600",
      "bg-cyan-600",
      "bg-amber-600",
      "bg-violet-600",
    ];
    const avatarColor = bgColors[colorIndex];

    // Mic line / Quoted message logic:
    // If empty/blank, default to "Wants a shout-out on the mic!"
    const hasCustomText = Boolean(shout.shoutOut?.trim());
    const hasAdditional = Boolean(shout.additionalInfo?.trim());
    const mainMsg =
      shout.shoutOut?.trim() ||
      shout.additionalInfo?.trim() ||
      "Wants a shout-out on the mic!";

    // Timing calculation
    const timeSource = shout.createdAt || shout.updatedAt;
    const createdTime = timeSource ? new Date(timeSource).getTime() : Date.now();
    const minutes = Math.max(0, Math.floor((Date.now() - createdTime) / 60000));
    const timeStr =
      minutes > 59
        ? `${Math.floor(minutes / 60)} hr${Math.floor(minutes / 60) > 1 ? "s" : ""}`
        : `${minutes} mins`;

    // Tip calculation
    const tipVal =
      shout.djTipValueInr ||
      (shout.djTipCoins ? Number(shout.djTipCoins) : 0) ||
      (shout.tipAmount ? Number(shout.tipAmount) : 0);
    const hasTip = tipVal > 0;
    const tipStr = tipVal % 1 === 0 ? tipVal : tipVal.toFixed(2);
    const isDelayed = minutes >= 15;

    return (
      <div className="w-full">
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-xs border border-gray-100/90 relative hover:shadow-md transition-shadow">
          {/* Header Row: Avatar, Guest Info & Time */}
          <div className="flex items-start justify-between gap-3 w-full">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {/* Avatar Circle */}
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-purple-100 shadow-2xs mt-0.5">
                {shout.user?.avatar ? (
                  <img
                    src={shout.user.avatar}
                    alt={guestName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`w-full h-full ${avatarColor} text-white font-bold flex items-center justify-center text-base shadow-inner`}
                  >
                    {initial}
                  </div>
                )}
              </div>

              {/* Guest Name & Highlighted Shout-Out Message */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0 flex-nowrap">
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-none truncate">
                    {guestName}
                  </h3>
                  {type !== "request" && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-bold shrink-0 whitespace-nowrap leading-none">
                      <Check className="w-3 h-3 text-green-600 stroke-[2.5]" />
                      <span>Announced</span>
                    </span>
                  )}
                </div>

                {/* Highlighted Shout-Out Text */}
                <div className="mt-1">
                  <span className="inline-block px-2.5 py-1 bg-purple-50/90 text-purple-950 font-bold text-sm sm:text-base rounded-lg border border-purple-100/90 leading-snug break-words shadow-2xs">
                    “{mainMsg}”
                  </span>
                </div>
              </div>
            </div>

            {/* Time / Waiting Badge */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold bg-gray-50 px-2.5 py-1 rounded-full border border-gray-200/70 flex-shrink-0 mt-0.5">
              <Clock className="w-3 h-3 text-gray-400" />
              <span>
                {minutes === 0
                  ? "Just now"
                  : minutes < 60
                    ? `${minutes}m ago`
                    : new Date(timeSource).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
              </span>
            </div>
          </div>

          {/* Optional Special Message section if both shoutOut and additionalInfo exist */}
          {hasCustomText && hasAdditional && shout.shoutOut !== shout.additionalInfo && (
            <div className="mt-2.5 px-3 py-2 bg-gray-50/80 border border-gray-200/80 rounded-xl flex items-start gap-2">
              <TbMessageFilled className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <span className="text-[11px] font-semibold text-gray-500 block">
                  Additional Note:
                </span>
                <p className="text-xs text-gray-700 leading-snug">
                  {shout.additionalInfo}
                </p>
              </div>
            </div>
          )}

          {/* Banner Section (Exact 3-case logic matching Android RequestShoutOutAdapter) */}
          {(hasTip || isDelayed) && (
            <div className="mt-3 w-full">
              {isDelayed && hasTip ? (
                /* Case 1: Both Delayed & Tip (Side-by-side pills) */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                  <div className="flex items-center gap-2.5 px-3 py-2 bg-orange-50 border border-orange-200 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">
                      <Clock className="w-3.5 h-3.5 animate-pulse" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-orange-950 leading-tight">
                        {timeStr} delay
                      </p>
                      <p className="text-[11px] text-orange-600 font-medium leading-tight">
                        Waiting in queue
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 flex-shrink-0">
                      <Coins className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-amber-950 leading-tight">
                        ₹{tipStr} tip added
                      </p>
                      <p className="text-[11px] text-amber-700 font-medium leading-tight">
                        Collect on announce
                      </p>
                    </div>
                  </div>
                </div>
              ) : hasTip ? (
                /* Case 2: Only Tip (Sleek, compact amber banner) */
                <div className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 bg-gradient-to-r from-amber-50 via-yellow-50/90 to-amber-50 border border-amber-200/90 rounded-xl shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 border border-amber-300/80 flex items-center justify-center text-amber-800 flex-shrink-0 shadow-2xs">
                      <Coins className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-nowrap leading-none">
                      <span className="text-sm sm:text-base font-bold text-amber-950 whitespace-nowrap">
                        ₹{tipStr} tip waiting for you! 🎉
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-amber-800/90 whitespace-nowrap truncate">
                        — Say it on mic to collect it
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] sm:text-xs font-bold text-amber-800 bg-amber-100/90 px-2.5 py-1 rounded-full border border-amber-200 flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span className="hidden sm:inline">Tip Reward</span>
                  </div>
                </div>
              ) : (
                /* Case 3: Only Delayed (Full-width compact orange banner) */
                <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 bg-orange-50 border border-orange-200/80 rounded-xl shadow-2xs">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0 shadow-2xs">
                    <Clock className="w-4 h-4 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-2 min-w-0 flex-1 flex-nowrap leading-none">
                    <span className="text-sm sm:text-base font-bold text-orange-950 whitespace-nowrap">
                      {timeStr} waiting ⏱
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-orange-600 whitespace-nowrap truncate">
                      — Try to announce this request soon
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action CTAs for Request */}
          {type === "request" && (
            <div className="mt-3.5 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2.5 w-full">
              <button
                className="w-full py-2.5 px-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition-all shadow-2xs"
                onClick={handleNotYet}
              >
                <X className="w-4 h-4 text-gray-500" />
                <span>Not Yet</span>
              </button>
              <button
                className="w-full py-2.5 px-3 bg-logo hover:opacity-95 active:scale-[0.99] text-white rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-2 shadow-sm shadow-purple-200 transition-all hover:scale-[1.01]"
                onClick={() => ShoutoutPlayed(shout._id)}
              >
                <Mic className="w-4 h-4" />
                <span>Announce Now</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);
